import React, { useEffect, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, Shuffle, Repeat, Heart, ListMusic, Maximize2, Mic2 } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';

const TRACK_TOTAL_SECONDS = 218; // 3:38 mock duration
const PROGRESS_TICK_MS = 1000;
const PROGRESS_MAX_PERCENT = 100;
const PERCENT_PER_SECOND = PROGRESS_MAX_PERCENT / TRACK_TOTAL_SECONDS;
const SECONDS_PER_MINUTE = 60;

const formatTime = (secs) => {
  const m = Math.floor(secs / SECONDS_PER_MINUTE);
  const s = Math.floor(secs % SECONDS_PER_MINUTE);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const PlayerBar = () => {
  const { current, isPlaying, togglePlay, progress, setProgress, volume, setVolume } = usePlayer();
  const [liked, setLiked] = useState(false);

  const currentSec = (progress / PROGRESS_MAX_PERCENT) * TRACK_TOTAL_SECONDS;

  useEffect(() => {
    if (!isPlaying) return undefined;
    const t = setInterval(() => {
      setProgress((p) => (p >= PROGRESS_MAX_PERCENT ? 0 : p + PERCENT_PER_SECOND));
    }, PROGRESS_TICK_MS);
    return () => clearInterval(t);
  }, [isPlaying, setProgress]);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 h-[90px] bg-gradient-to-t from-[#050506] to-[#0a0a0b] border-t border-white/5">
      <div className="h-full grid grid-cols-3 items-center px-4 gap-4">
        {/* Now Playing */}
        <div className="flex items-center gap-3 min-w-0">
          {current ? (
            <>
              <img src={current.cover} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-white line-clamp-1">{current.title}</div>
                <div className="text-xs text-neutral-400 line-clamp-1">{current.artist}</div>
              </div>
              <button
                onClick={() => setLiked((l) => !l)}
                className={`ml-2 transition-colors ${liked ? 'text-orange-400' : 'text-neutral-400 hover:text-white'}`}
              >
                <Heart className="w-4 h-4" fill={liked ? 'currentColor' : 'none'} />
              </button>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded bg-white/5 border border-white/5 flex items-center justify-center">
                <ListMusic className="w-5 h-5 text-neutral-500" />
              </div>
              <div className="min-w-0">
                <div className="text-sm text-neutral-400">Nothing playing</div>
                <div className="text-xs text-neutral-600">Pick a mix to start</div>
              </div>
            </>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="flex items-center gap-4">
            <button className="text-neutral-400 hover:text-white transition-colors">
              <Shuffle className="w-4 h-4" />
            </button>
            <button className="text-neutral-400 hover:text-white transition-colors">
              <SkipBack className="w-5 h-5" fill="currentColor" />
            </button>
            <button
              onClick={togglePlay}
              disabled={!current}
              className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-40"
            >
              {isPlaying ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
            </button>
            <button className="text-neutral-400 hover:text-white transition-colors">
              <SkipForward className="w-5 h-5" fill="currentColor" />
            </button>
            <button className="text-neutral-400 hover:text-white transition-colors">
              <Repeat className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 w-full max-w-[500px]">
            <span className="text-[11px] text-neutral-500 w-10 text-right">{formatTime(currentSec)}</span>
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
              className="flex-1 h-1 accent-orange-500 bg-white/10 rounded-full appearance-none cursor-pointer"
            />
            <span className="text-[11px] text-neutral-500 w-10">{formatTime(TRACK_TOTAL_SECONDS)}</span>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center justify-end gap-3">
          <button className="text-neutral-400 hover:text-white transition-colors">
            <Mic2 className="w-4 h-4" />
          </button>
          <button className="text-neutral-400 hover:text-white transition-colors">
            <ListMusic className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-neutral-400" />
            <input
              type="range"
              min={0}
              max={100}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="w-24 h-1 accent-orange-500 bg-white/10 rounded-full appearance-none cursor-pointer"
            />
          </div>
          <button className="text-neutral-400 hover:text-white transition-colors">
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerBar;
